/** The square every connector logo is stored at. */
export const LOGO_SIZE = 152

const SVG_NAMESPACE = "http://www.w3.org/2000/svg"

export function isSVG(file: File): boolean {
  return file.type === "image/svg+xml" || file.name.toLowerCase().endsWith(".svg")
}

/**
 * logoDataURL turns a stored logo into something an img can display. Logos
 * arrive as bare base64, but a freshly picked one is already a data url.
 */
export function logoDataURL(b64: string | null | undefined): string | null {
  if (!b64) {
    return null
  }

  return b64.startsWith("data:") ? b64 : `data:image/png;base64,${b64}`
}

export type Size = {
  width: number
  height: number
}

/**
 * svgLength reads an SVG width or height attribute. Only the ratio between the
 * two matters here - both are written in the same unit in practice - so the
 * unit is dropped rather than converted. A percentage says nothing about the
 * intrinsic size at all, so it is rejected.
 */
export function svgLength(raw: string | null): number | null {
  if (raw === null) {
    return null
  }

  const trimmed = raw.trim()
  if (trimmed === "" || trimmed.endsWith("%")) {
    return null
  }

  const value = parseFloat(trimmed)
  if (!Number.isFinite(value) || value <= 0) {
    return null
  }

  return value
}

/**
 * svgViewBoxSize reads the width and height out of a viewBox, which is where
 * responsive SVGs - the ones with no width and height of their own - keep their
 * proportions.
 */
export function svgViewBoxSize(raw: string | null): Size | null {
  if (raw === null) {
    return null
  }

  const parts = raw
    .trim()
    .split(/[\s,]+/)
    .map((part) => parseFloat(part))

  if (parts.length !== 4 || parts.some((part) => !Number.isFinite(part))) {
    return null
  }

  const [, , width, height] = parts
  if (width <= 0 || height <= 0) {
    return null
  }

  return { width, height }
}

/**
 * svgIntrinsicSize works out what an SVG's proportions are: its own width and
 * height when it declares them, otherwise its viewBox. An SVG that offers
 * neither has no proportions to preserve, so it is treated as square.
 */
export function svgIntrinsicSize(svg: Element): Size {
  const width = svgLength(svg.getAttribute("width"))
  const height = svgLength(svg.getAttribute("height"))
  if (width !== null && height !== null) {
    return { width, height }
  }

  const viewBox = svgViewBoxSize(svg.getAttribute("viewBox"))
  if (viewBox) {
    return viewBox
  }

  return { width: LOGO_SIZE, height: LOGO_SIZE }
}

/** containedSize scales a size to fit inside a square without distorting it. */
export function containedSize(size: Size, bound: number): Size {
  const scale = Math.min(bound / size.width, bound / size.height)

  return {
    width: Math.max(1, Math.round(size.width * scale)),
    height: Math.max(1, Math.round(size.height * scale)),
  }
}

export type PreparedSVG = Size & {
  markup: string
}

/**
 * prepareSVG rewrites an uploaded SVG to render at the size it will occupy in
 * the logo square. Drawing it at its target size rather than rasterizing at
 * whatever size it declares and scaling afterwards is what keeps a vector logo
 * sharp.
 */
export function prepareSVG(markup: string, bound: number = LOGO_SIZE): PreparedSVG {
  const doc = new DOMParser().parseFromString(markup, "image/svg+xml")

  if (doc.getElementsByTagName("parsererror").length > 0) {
    throw new Error("That file is not valid SVG")
  }

  const svg = doc.documentElement
  if (!svg || svg.nodeName.toLowerCase() !== "svg") {
    throw new Error("That file is not an SVG image")
  }

  const intrinsic = svgIntrinsicSize(svg)
  const size = containedSize(intrinsic, bound)

  // Without a viewBox the new width and height would crop rather than scale,
  // and without the namespace the browser refuses to render it at all.
  if (!svgViewBoxSize(svg.getAttribute("viewBox"))) {
    svg.setAttribute("viewBox", `0 0 ${intrinsic.width} ${intrinsic.height}`)
  }
  if (!svg.getAttribute("xmlns")) {
    svg.setAttribute("xmlns", SVG_NAMESPACE)
  }

  svg.setAttribute("width", String(size.width))
  svg.setAttribute("height", String(size.height))

  return { markup: new XMLSerializer().serializeToString(svg), ...size }
}

type Drawable = Size & {
  source: CanvasImageSource
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new window.Image()
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error("That image could not be read"))
    image.src = url
  })
}

async function drawableFromSVG(file: File, bound: number): Promise<Drawable> {
  const prepared = prepareSVG(await file.text(), bound)

  const url = URL.createObjectURL(new Blob([prepared.markup], { type: "image/svg+xml" }))
  try {
    return {
      source: await loadImage(url),
      width: prepared.width,
      height: prepared.height,
    }
  } finally {
    URL.revokeObjectURL(url)
  }
}

async function drawable(file: File, bound: number): Promise<Drawable> {
  // Browsers do not agree on decoding SVG through createImageBitmap, so vectors
  // take the img element route instead.
  if (isSVG(file)) {
    return drawableFromSVG(file, bound)
  }

  const bitmap = await createImageBitmap(file)
  return { source: bitmap, width: bitmap.width, height: bitmap.height }
}

/**
 * hasPixels reports whether anything was actually painted. An SVG that leans on
 * a script, an external font, or a remote image draws nothing at all inside an
 * img element, and a blank logo is worse than a rejected upload.
 */
function hasPixels(ctx: CanvasRenderingContext2D, size: number): boolean {
  const { data } = ctx.getImageData(0, 0, size, size)

  for (let i = 3; i < data.length; i += 4) {
    if (data[i] !== 0) {
      return true
    }
  }

  return false
}

/**
 * rasterizeToLogo redraws an uploaded image - raster or vector - as a square
 * PNG of the size every connector logo is stored at, and returns it base64
 * encoded without the data url prefix, which is how the API carries it.
 */
export async function rasterizeToLogo(file: File, size: number = LOGO_SIZE): Promise<string> {
  const image = await drawable(file, size)

  const canvas = document.createElement("canvas")
  canvas.width = size
  canvas.height = size

  const ctx = canvas.getContext("2d")
  if (!ctx) {
    throw new Error("Could not prepare the image canvas")
  }

  // Fit the whole logo inside the square and centre it, so a wide wordmark is
  // letterboxed rather than cropped or stretched.
  const fitted = containedSize(image, size)

  ctx.drawImage(image.source, (size - fitted.width) / 2, (size - fitted.height) / 2, fitted.width, fitted.height)

  if (!hasPixels(ctx, size)) {
    throw new Error(
      "That image rendered blank. SVGs that rely on scripts, external fonts, or remote images cannot be used.",
    )
  }

  return canvas.toDataURL("image/png").replace(/^data:image\/png;base64,/, "")
}
